// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Dimension conversions between metric and imperial.
 * @author James Reid
 */

// @ts-check

// @@imports-types
import {
    MetricMeasurement,
    ImperialMeasurement
} from "../../types/index.js"

// @@body
// Conversion constants.
const INCHES_IN_METRE = 39.3701
const INCHES_IN_FOOT = 12

/**
 * Convert a single imperial measurement to a metric measurement.
 * @param {ImperialMeasurement} measurement Imperial measurement to convert.
 * @returns {MetricMeasurement} Converted metric measurement.
 */
const imperialToMetric = ({ feet = 0, inches = 0 } = {}) => {
    return (feet * INCHES_IN_FOOT + inches) / INCHES_IN_METRE
}

/**
 * Convert a single square imperial measurement to a square metric measurement.
 * @param {ImperialMeasurement} measurement Square imperial measurement to
 * convert. Note that any inches quantity provided will be considered as a
 * decimal part of the feet quantity (i.e. 4'6" as an input will be considered
 * as 4.5 sq ft for conversion).
 * @returns {MetricMeasurement} Converted metric measurement.
 */
const squareImperialToMetric = ({ feet = 0, inches = 0 } = {}) => {
    return (feet * INCHES_IN_FOOT + inches) * INCHES_IN_FOOT /
        (INCHES_IN_METRE * INCHES_IN_METRE)
}

/**
 * Convert a single metric measurement to an imperial measurement.
 * @param {MetricMeasurement} measurement Metric measurement to convert.
 * @param {boolean} decimal Should conversion return a decimal feet quantity,
 * ignoring inches part of conversion.
 * @returns {ImperialMeasurement} Converted imperial measurement.
 */
const metricToImperial = (measurement, decimal = false) => {
    const inches = measurement * INCHES_IN_METRE
    const feet = inches / INCHES_IN_FOOT
    const floorFeet = Math.floor(feet)

    return decimal
        ? { feet, inches: 0 }
        : { feet: floorFeet, inches: inches - floorFeet * INCHES_IN_FOOT }
}

/**
 * Convert a single square metric measurement to a square imperial measurement.
 * @param {MetricMeasurement} measurement Square metric measurement to
 * convert. Note that any inches quantity returned should be considered as a
 * decimal part of the feet quantity (i.e. 4'6" returned should be considered
 * as 4.5 sq ft, which is equivalent to 4 sq ft and ~55 sq in).
 * @param {boolean} decimal Should conversion return a decimal feet quantity,
 * ignoring inches part of conversion.
 * @returns {ImperialMeasurement} Converted imperial measurement.
 */
const squareMetricToImperial = (measurement, decimal = true) => {
    const inches = measurement * INCHES_IN_METRE * INCHES_IN_METRE
    const feet = inches / (INCHES_IN_FOOT * INCHES_IN_FOOT)
    const floorFeet = Math.floor(feet)

    return decimal
        ? { feet, inches: 0 }
        : { feet: floorFeet, inches: (feet - floorFeet) * INCHES_IN_FOOT }
}

/**
 * Convert any measurements passed to function arguments to metric.
 * @param {MetricMeasurement[]|ImperialMeasurement[]} measurements Array of
 * mixed metric and imperial measurements to convert.
 * @returns {MetricMeasurement[]} Array of converted metric measurements.
 */
const measurementsToMetric = measurements => {
    const metricMeasurements = []

    // Convert measurements to metric if required (existing metric measurements
    // will be of type "number").
    for (const measurement of measurements) {
        typeof measurement === "number" ? metricMeasurements.push(measurement)
            : metricMeasurements.push(imperialToMetric(measurement))
    }

    return metricMeasurements
}

/**
 * Convert any squared measurements passed to function arguments to metric.
 * @param {MetricMeasurement[]|ImperialMeasurement[]} measurements Array of
 * mixed squared metric and imperial measurements to convert.
 * @returns {MetricMeasurement[]} Array of converted metric measurements.
 */
const squareMeasurementsToMetric = measurements => {
    const metricMeasurements = []

    // Convert measurements to metric if required (existing metric measurements
    // will be of type "number").
    for (const measurement of measurements) {
        typeof measurement === "number" ? metricMeasurements.push(measurement)
            : metricMeasurements.push(squareImperialToMetric(measurement))
    }

    return metricMeasurements
}

/**
 * Convert any measurements passed to function arguments to imperial.
 * @param {MetricMeasurement[]|ImperialMeasurement[]} measurements Array of
 * mixed metric and imperial measurements to convert.
 * @param {boolean} decimal Should conversion return a decimal feet quantity,
 * ignoring inches part of conversion.
 * @returns {ImperialMeasurement[]} Array of converted imperial measurements.
 */
const measurementsToImperial = (measurements, decimal = false) => {
    const imperialMeasurements = []

    // Convert measurements to imperial if required (existing imperial
    // measurements will be of type "number").
    for (const measurement of measurements) {
        typeof measurement === "object" ? imperialMeasurements.push(measurement)
            : imperialMeasurements
                .push(metricToImperial(measurement, decimal))
    }

    return imperialMeasurements
}

/**
 * Convert any squared measurements passed to function arguments to imperial.
 * @param {MetricMeasurement[]|ImperialMeasurement[]} measurements Array of
 * mixed squared metric and imperial measurements to convert.
 * @param {boolean} decimal Should conversion return a decimal feet quantity,
 * ignoring inches part of conversion.
 * @returns {ImperialMeasurement[]} Array of converted imperial measurements.
 */
const squareMeasurementsToImperial = (measurements, decimal = true) => {
    const imperialMeasurements = []

    // Convert measurements to imperial if required (existing imperial
    // measurements will be of type "number").
    for (const measurement of measurements) {
        typeof measurement === "object" ? imperialMeasurements.push(measurement)
            : imperialMeasurements
                .push(squareMetricToImperial(measurement, decimal))
    }

    return imperialMeasurements
}

// @@exports
export {
    imperialToMetric,
    squareImperialToMetric,
    metricToImperial,
    squareMetricToImperial,
    measurementsToMetric,
    squareMeasurementsToMetric,
    measurementsToImperial,
    squareMeasurementsToImperial
}
